import { Field, Select, SelectSizeVariant } from '@payaca/ambrosia';
import { FC, useCallback, useEffect } from 'react';

import { PermissionGuard } from '../permissionGuard/PermissionGuard';

import useGetMyAccountAccountingIntegrations from '@/api/queries/me/useGetMyAccountAccountingIntegrations';
import useGetProjectAccountingIntegrationCategories from '@/api/queries/project/useGetProjectAccountingIntegrationCategories';

import useUpdateProject from '@/api/mutations/project/useUpdateProject';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export const ProjectAccountingIntegrationCategoriesControl: FC<{
  projectId: string;
}> = ({ projectId }) => {
  const { data, isLoading } = useGetMyAccountAccountingIntegrations();

  const { accountingIntegrationCategories, isLoading: isCategoriesLoading } =
    useGetProjectAccountingIntegrationCategories(projectId);

  if (isLoading || isCategoriesLoading) {
    return null;
  }

  if (!data?.quickbooks?.categories.length && !data?.xero?.categories.length) {
    return null;
  }

  if (!accountingIntegrationCategories) {
    return null;
  }

  return (
    <PermissionGuard
      renderIfHasPermissions={[
        AccountsPermissions.UPDATE_CONNECTION_ACCOUNTING,
      ]}
    >
      <CategoriesForm
        projectId={projectId}
        integrationData={data}
        accountingIntegrationCategories={accountingIntegrationCategories}
      />
    </PermissionGuard>
  );
};

interface CategoriesFormProps {
  projectId: string;
  integrationData: NonNullable<
    ReturnType<typeof useGetMyAccountAccountingIntegrations>['data']
  >;
  accountingIntegrationCategories: NonNullable<
    ReturnType<
      typeof useGetProjectAccountingIntegrationCategories
    >['accountingIntegrationCategories']
  >;
}

const CategoriesForm: FC<CategoriesFormProps> = ({
  projectId,
  integrationData,
  accountingIntegrationCategories,
}) => {
  const { mutateAsync: updateProjectMutation } = useUpdateProject();

  const formMethods = useForm({
    defaultValues: {
      quickbooks: accountingIntegrationCategories.quickbooks!.reduce(
        (acc: Record<string, string | null>, elem) => {
          acc[elem.categoryIdentifier] = elem.optionIdentifier || null;
          return acc;
        },
        {}
      ),
      xero: accountingIntegrationCategories.xero!.reduce(
        (acc: Record<string, string | null>, elem) => {
          acc[elem.categoryIdentifier] = elem.optionIdentifier || null;
          return acc;
        },
        {}
      ),
    },
  });

  const { watch, handleSubmit } = formMethods;
  const onSubmit = useCallback<Parameters<typeof handleSubmit>[0]>(
    ({ quickbooks, xero }) => {
      const quickbooksInput = quickbooks
        ? Object.entries(quickbooks).map(([key, value]) => {
            return {
              categoryIdentifier: key,
              optionIdentifier: value,
            };
          })
        : [];

      const xeroInput = xero
        ? Object.entries(xero).map(([key, value]) => {
            return {
              categoryIdentifier: key,
              optionIdentifier: value || null,
            };
          })
        : [];

      void updateProjectMutation({
        projectId,
        accountingIntegrationCategories: {
          quickbooks: quickbooksInput,
          xero: xeroInput,
        },
      });
    },
    [updateProjectMutation, projectId]
  );
  // Auto-submit form on change
  useEffect(() => {
    const subscription = watch(() => void handleSubmit(onSubmit)());
    return () => subscription.unsubscribe();
  }, [handleSubmit, onSubmit, watch]);

  return (
    <div className="space-y-2">
      <FormProvider {...formMethods}>
        {integrationData.quickbooks?.categories.map((c) => {
          return (
            <Field.Legacy key={c.identifier}>
              <Field.Label className="">Quickbooks: {c.name}</Field.Label>
              <Controller
                name={`quickbooks.${c.identifier}`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <Select
                      // This list is unbounded in length and triggers a Headless
                      // UI bug when it goes overflows the page
                      animate={false}
                      sizeVariant={SelectSizeVariant.SM}
                      options={c.options.map((o) => ({
                        value: o.identifier,
                        label: o.name,
                      }))}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Field.Legacy>
          );
        })}
        {integrationData.xero?.categories.map((c) => {
          return (
            <Field.Legacy key={c.identifier}>
              <Field.Label>Xero: {c.name}</Field.Label>
              <Controller
                name={`xero.${c.identifier}`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <Select
                      // This list is unbounded in length and triggers a Headless
                      // UI bug when it goes overflows the page
                      animate={false}
                      sizeVariant={SelectSizeVariant.SM}
                      options={c.options.map((o) => ({
                        value: o.identifier,
                        label: o.name,
                      }))}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Field.Legacy>
          );
        })}
      </FormProvider>
    </div>
  );
};
